import { useEffect, useRef, useState } from 'react';
import { base64ToArrayBuffer } from '../components/common/DocumentUpload';

export const PREVIEWABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
export const UNSUPPORTED_IMAGE_EXTENSIONS = ['heic', 'heif', 'svg'];

const MIME_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
};

export const getFileExtension = (filenameOrKey) => {
  if (!filenameOrKey || typeof filenameOrKey !== 'string') {
    return '';
  }
  const filename = filenameOrKey.split('/').pop() || '';
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === filename.length - 1) {
    return '';
  }
  return filename.slice(dotIndex + 1).toLowerCase();
};

export const isPreviewableImage = (filenameOrKey) =>
  PREVIEWABLE_IMAGE_EXTENSIONS.includes(getFileExtension(filenameOrKey));

export const isUnsupportedImagePreview = (filenameOrKey) =>
  UNSUPPORTED_IMAGE_EXTENSIONS.includes(getFileExtension(filenameOrKey));

export const getMimeTypeFromFilename = (filenameOrKey) => {
  const extension = getFileExtension(filenameOrKey);
  return MIME_BY_EXTENSION[extension] || 'application/octet-stream';
};

export const blobFromBase64Download = (base64Data, fileName) => {
  const byteArray = base64ToArrayBuffer(base64Data);
  return new Blob([byteArray], { type: getMimeTypeFromFilename(fileName) });
};

export const deliverDownloadedFile = (base64Data, fileName, onBlob, { triggerDownload = true } = {}) => {
  if (!base64Data) {
    if (onBlob) {
      onBlob(null);
    }
    return null;
  }

  const blob = blobFromBase64Download(base64Data, fileName);

  if (triggerDownload && fileName) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
  }

  if (onBlob) {
    onBlob(blob);
  }
  return blob;
};

export const useInView = ({ rootMargin = '200px', threshold = 0.01 } = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || inView) {
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [inView, rootMargin, threshold]);

  return [ref, inView];
};
